import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { ProfileBaseline } from '@/shared/api/generated/catalog';
import { Alert } from '@/shared/ui/Alert';

/* The baseline figure, the word provisional, and the deductions it is made of.
 *
 * **The arithmetic is the server's, and that changed here.** It lived in this feature's model while
 * one screen rendered it. The score listing needs the same figure over a page of fifty datasets,
 * which a sketch read per row cannot pay for — so it moved to `core.catalog`'s domain and both
 * surfaces read what the API computed. Keeping a copy here would be two answers to "what does this
 * dataset score before any rule runs", which is the failure this codebase records at five sites and
 * which matters more than usual for a *score*: both copies would pass their own tests while
 * disagreeing about a number a customer is shown.
 *
 * What stays here is the *wording*. A finding arrives as a `kind` rather than a sentence, so the
 * platform's own vocabulary is what crosses the wire and the copy catalogue is what a reader sees —
 * which is what makes the figure translatable.
 */

export function BaselineSummary({
  baseline,
}: {
  readonly baseline: ProfileBaseline;
}): ReactElement {
  const { t } = useTranslation();

  /* Nothing measurable is *not* a zero, and the panel says which. An empty relation has no
     baseline; reporting one would be the platform inventing a claim the profile did not make. */
  if (baseline.value === null) {
    return (
      <Alert tone="notice" title={t('profile.baseline.unmeasuredTitle')}>
        {t('profile.baseline.unmeasured', {
          columns: baseline.unmeasured_columns.length,
        })}
      </Alert>
    );
  }

  return (
    <div className="lq-baseline">
      <div className="lq-baseline__head">
        <span className="lq-baseline__value lumiq-numeric">{baseline.value}</span>
        <div>
          <p className="lq-baseline__label">{t('profile.baseline.label')}</p>
          {/* Provisional, and *why* — a reader who is told "provisional" without the reason
              reasonably ignores the word. Coverage is in the same sentence for the reason the
              scores listing states: a figure over four columns is a figure about four columns. */}
          <p className="lq-muted lq-baseline__hint">
            {t('profile.baseline.provisional', { columns: baseline.columns_measured })}
          </p>
        </div>
      </div>

      {baseline.findings.length === 0 ? (
        <p className="lq-muted lq-baseline__hint">{t('profile.baseline.nothingFound')}</p>
      ) : (
        <ul className="lq-baseline__findings">
          {baseline.findings.map((finding) => (
            <li key={`${finding.column}:${finding.kind}`}>
              <span className="lq-baseline__deduction lumiq-numeric">
                {t('profile.baseline.deduction', { points: finding.deduction })}
              </span>
              <span className="lq-mono">{finding.column}</span>
              <span>
                {t(`profile.finding.${finding.kind}`, { observed: finding.observed })}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Unmeasured columns are named even when some others scored, because a baseline over eight of
          twelve columns is a different claim from one over twelve. */}
      {baseline.unmeasured_columns.length > 0 && (
        <p className="lq-muted lq-baseline__hint">
          {t('profile.baseline.someUnmeasured', {
            columns: baseline.unmeasured_columns.length,
          })}
        </p>
      )}
    </div>
  );
}
